import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { roleService } from '../../services/api/role.service';
import { userService } from '../../services/api/user.service';

// Async thunk to fetch user roles
export const fetchUserRoles = createAsyncThunk(
  'permissions/fetchUserRoles',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await userService.getUserRoles(userId);
      return response.data.data || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch user roles');
    }
  }
);

// Async thunk to fetch role permissions (legacy - for backward compatibility)
export const fetchRolePermissions = createAsyncThunk(
  'permissions/fetchRolePermissions',
  async (roleId, { rejectWithValue }) => {
    try {
      const response = await roleService.getRolePermissions(roleId);
      
      // Extract only the necessary data to avoid non-serializable values
      if (response && response.data) {
        return {
          data: response.data,
          status: response.status,
          statusText: response.statusText
        };
      }
      
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch role permissions');
    }
  }
);

// Async thunk to fetch user effective permissions (new endpoint)
// Uses condition to prevent duplicate requests for the same role
export const fetchUserEffectivePermissions = createAsyncThunk(
  'permissions/fetchUserEffectivePermissions',
  async ({ userId, roleId }, { rejectWithValue }) => {
    try {
      const response = await userService.getUserEffectivePermissions(userId, roleId);
      
      // Extract only the necessary data to avoid non-serializable values
      if (response && response.data) {
        return {
          data: response.data,
          status: response.status,
          statusText: response.statusText,
          roleId: roleId // Include roleId in response for tracking
        };
      }
      
      return { ...response, roleId };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch user effective permissions');
    }
  },
  {
    // Prevent duplicate requests - only allow one request at a time
    condition: ({ userId, roleId }, { getState }) => {
      const { permissions } = getState();
      // Don't fetch if already loading or if we already have permissions for this role
      if (permissions.isLoading) {
        return false;
      }
      // Allow fetch if role changed
      if (permissions.lastFetchedRoleId === roleId) {
        return false;
      }
      return true;
    }
  }
);

const initialState = {
  userRoles: [],
  menuPermissions: [],
  isLoading: false,
  error: null,
  lastFetchedRoleId: null, // Track the last fetched role to prevent duplicate requests
};

// Helper function to log objects safely
const safeLog = (label, obj) => {
  try {
    } catch (e) {
    }
};

const permissionsSlice = createSlice({
  name: 'permissions',
  initialState,
  reducers: {
    clearPermissions: (state) => {
      state.userRoles = [];
      state.menuPermissions = [];
      state.error = null;
      state.lastFetchedRoleId = null; // Reset on clear to allow fresh fetch
    },
    // Add a manual action to set menu permissions for debugging
    setMenuPermissions: (state, action) => {
      state.menuPermissions = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // Handle fetchUserRoles
      .addCase(fetchUserRoles.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUserRoles.fulfilled, (state, action) => {
        state.isLoading = false;
        state.userRoles = action.payload;
      })
      .addCase(fetchUserRoles.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Handle fetchRolePermissions
      .addCase(fetchRolePermissions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchRolePermissions.fulfilled, (state, action) => {
        state.isLoading = false;
        // Extract permissions from the response
        const responseData = action.payload;
        
        if (!responseData) {
          state.error = 'Invalid response format';
          state.menuPermissions = [];
          return;
        }
        
        // Direct access to the permissions array from the API response
        if (Array.isArray(responseData)) {
          // The API is returning the permissions array directly with hierarchical structure
          state.menuPermissions = responseData;
          state.error = null;
          return;
        }
        
        // Handle nested response format if the direct format isn't found
        if (responseData.data) {
          // Check if the response is the new API format with data property containing success, statusCode, etc.
          if (responseData.data.success !== undefined && responseData.data.data) {
            const apiData = responseData.data;
            
            // Check if the data array contains roles with permissions
            if (Array.isArray(apiData.data) && apiData.data.length > 0) {
              // Get the first role's permissions
              const roleData = apiData.data[0];
              
              if (roleData && roleData.permissions && Array.isArray(roleData.permissions)) {
                // Process permissions with the new hierarchical structure
                const processPermission = (permission) => {
                  if (!permission.am_menu_configuration) return null;
                  
                  const menu = permission.am_menu_configuration;
                  const result = {
                    ...permission,
                    id: menu.menu_code,
                    title: menu.menu_display_name || menu.menu_name,
                    menuId: menu.menu_id,
                    parentMenuId: menu.parent_menu_id,
                    path: menu.route_url,
                    classIcon: menu.icon_class || '',
                    isRestricted: permission.is_restricted || false,
                    isActive: permission.is_active !== false,
                    children: []
                  };
                  
                  // Process children recursively if they exist
                  if (permission.children && Array.isArray(permission.children)) {
                    result.children = permission.children
                      .map(processPermission)
                      .filter(Boolean);
                  }
                  
                  return result;
                };
                
                const processedPermissions = roleData.permissions
                  .map(processPermission)
                  .filter(Boolean);
                
                state.menuPermissions = processedPermissions;
                state.error = null;
                return;
              }
            }
          } else {
            // Handle the old API format
            const apiData = responseData.data;
            
            if (apiData.success && apiData.data && Array.isArray(apiData.data)) {
              // Process the first role's permissions
              if (apiData.data[0] && apiData.data[0].role && apiData.data[0].permissions) {
                const roleData = apiData.data[0];
                
                // If permissions already have a hierarchical structure with children
                if (roleData.permissions[0]?.children !== undefined) {
                  state.menuPermissions = roleData.permissions;
                  state.error = null;
                  return;
                }
                
                // New API format with nested children structure
                if (roleData.permissions.some(p => p.children)) {
                  // Recursive function to process permissions with children
                  const processPermission = (permission) => {
                    if (!permission.am_menu_configuration) return null;
                    
                    const menu = permission.am_menu_configuration;
                    const result = {
                      ...permission,
                      id: menu.menu_code,
                      title: menu.menu_display_name || menu.menu_name,
                      menuId: menu.menu_id,
                      parentMenuId: menu.parent_menu_id,
                      path: menu.route_url,
                      classIcon: menu.icon_class || '',
                      children: []
                    };
                    
                    // Process children recursively if they exist
                    if (permission.children && permission.children.length > 0) {
                      result.children = permission.children
                        .map(processPermission)
                        .filter(Boolean);
                    }
                    
                    return result;
                  };
                  
                  const processedPermissions = roleData.permissions
                    .map(processPermission)
                    .filter(Boolean);
                  
                  state.menuPermissions = processedPermissions;
                  state.error = null;
                  return;
                }
                
                // Otherwise, process them to create a hierarchical structure
                const menuItems = roleData.permissions.map(permission => {
                  // Check if we have the old format with am_menu_configuration
                  const menu = permission.am_menu_configuration || permission;
                  
                  if (!menu || !menu.menu_code) {
                    return null;
                  }
                  
                  return {
                    id: menu.menu_code,
                    title: menu.menu_display_name || menu.menu_name,
                    menuId: menu.menu_id,
                    parentMenuId: menu.parent_menu_id,
                    path: menu.route_url,
                    classIcon: menu.icon_class || '',
                    isRestricted: permission.is_restricted || false,
                    isActive: permission.is_active !== false,
                    children: []
                  };
                }).filter(Boolean);
                
                // Process parent-child relationships
                const menuMap = {};
                menuItems.forEach(item => {
                  menuMap[item.menuId] = item;
                });
                
                const rootMenus = [];
                menuItems.forEach(item => {
                  if (item.parentMenuId && menuMap[item.parentMenuId]) {
                    if (!menuMap[item.parentMenuId].children) {
                      menuMap[item.parentMenuId].children = [];
                    }
                    menuMap[item.parentMenuId].children.push(item);
                  } else {
                    rootMenus.push(item);
                  }
                });
                
                state.menuPermissions = rootMenus.length > 0 ? rootMenus : menuItems;
                state.error = null;
                return;
              }
            }
          }
        }
        
        // If we reach here, we didn't find valid permissions
        state.menuPermissions = [];
        state.error = null;
      })
      .addCase(fetchRolePermissions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Handle fetchUserEffectivePermissions (same logic as fetchRolePermissions)
      .addCase(fetchUserEffectivePermissions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUserEffectivePermissions.fulfilled, (state, action) => {
        state.isLoading = false;
        // Track the role ID that was fetched to prevent duplicate requests
        if (action.payload?.roleId) {
          state.lastFetchedRoleId = action.payload.roleId;
        }
        // Use the same processing logic as fetchRolePermissions
        const responseData = action.payload;
        
        if (!responseData) {
          state.error = 'Invalid response format';
          state.menuPermissions = [];
          return;
        }
        
        // Direct access to the permissions array from the API response
        if (Array.isArray(responseData)) {
          state.menuPermissions = responseData;
          state.error = null;
          return;
        }
        
        // Handle nested response format
        if (responseData.data) {
          // Check if the response is the new API format with data property
          if (responseData.data.success !== undefined && responseData.data.data) {
            const apiData = responseData.data;
            
            // Check if the data array contains roles with permissions
            if (Array.isArray(apiData.data) && apiData.data.length > 0) {
              const roleData = apiData.data[0];
              
              if (roleData && roleData.permissions && Array.isArray(roleData.permissions)) {
                const processPermission = (permission) => {
                  if (!permission.am_menu_configuration) return null;
                  
                  const menu = permission.am_menu_configuration;
                  const result = {
                    ...permission,
                    id: menu.menu_code,
                    title: menu.menu_display_name || menu.menu_name,
                    menuId: menu.menu_id,
                    parentMenuId: menu.parent_menu_id,
                    path: menu.route_url,
                    classIcon: menu.icon_class || '',
                    isRestricted: permission.is_restricted || false,
                    isActive: permission.is_active !== false,
                    children: []
                  };
                  
                  if (permission.children && Array.isArray(permission.children)) {
                    result.children = permission.children
                      .map(processPermission)
                      .filter(Boolean);
                  }
                  
                  return result;
                };
                
                const processedPermissions = roleData.permissions
                  .map(processPermission)
                  .filter(Boolean);
                
                state.menuPermissions = processedPermissions;
                state.error = null;
                return;
              }
            }
          }
        }
        
        // If we reach here, we didn't find valid permissions
        state.menuPermissions = [];
        state.error = null;
      })
      .addCase(fetchUserEffectivePermissions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearPermissions, setMenuPermissions } = permissionsSlice.actions;
export default permissionsSlice.reducer;
